import { FocusModeContext } from '@contexts/FocusModeContext'
import { useRouter } from 'next/router'
import { type FC, type ReactNode, type RefObject, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { FaCompress } from 'react-icons/fa6'
import ToolbarButton from './ToolbarButton'

type FocusModeOverlayProps = {
  children: ReactNode
  onExit: () => void
  scrollContainerRef: RefObject<HTMLDivElement | null>
}

/**
 * Renders a fullscreen reading overlay via a React Portal.
 * Provides FocusModeContext=true so children (PageInfoRow, SoundPlayer, etc.) hide themselves.
 * A small exit button sits in the top-left corner.
 */
const FocusModeOverlay: FC<FocusModeOverlayProps> = ({
  children,
  onExit,
  scrollContainerRef,
}) => {
  const { asPath } = useRouter()

  // Scroll the overlay container to top on page navigation
  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0)
  }, [asPath, scrollContainerRef])

  if (typeof window === 'undefined') return null

  return createPortal(
    <FocusModeContext.Provider value={true}>
      <div
        ref={scrollContainerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Odak modu"
        className="fixed inset-0 z-50 bg-white dark:bg-primary-dark overflow-y-auto"
      >
        {/* Floating exit button — top left, matches the enter button style */}
        <div className="fixed top-3 left-3 z-10">
          <ToolbarButton
            onClick={onExit}
            ariaLabel="Odak modundan çık (Esc)"
            title="Odak modundan çık (Esc)"
            className="text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <FaCompress />
          </ToolbarButton>
        </div>

        {/* Pure reading content */}
        <div className="mx-auto w-full max-w-3xl px-3 sm:px-4 pt-10 pb-16">
          {children}
        </div>
      </div>
    </FocusModeContext.Provider>,
    document.body,
  )
}

export default FocusModeOverlay
